import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Globe, Save, Loader2, Gamepad2, UserCircle2, Info } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { GAMES } from '../context/GameContext';
import toast from 'react-hot-toast';
import { COUNTRIES } from '../lib/countries';
import { useTranslation } from 'react-i18next';

const NICKNAME_CHECKED_KEY = 'emss_nickname_checked';

const CountryCheckModal: React.FC = () => {
  const { emssUser, updateProfile } = useAuth();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedGameId, setSelectedGameId] = useState('');
  const [nickname, setNickname] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!emssUser) return;

    const hasCountry = !!emssUser.country_code;
    const hasGame = !!emssUser.priority_game_id;
    const hasPolicy = !!emssUser.privacy_policy_accepted_at;
    const nicknameChecked = localStorage.getItem(NICKNAME_CHECKED_KEY) === emssUser.id;

    // Don't show if user has country + game AND has already accepted policy / confirmed nickname
    const isCompleted = hasCountry && hasGame && (hasPolicy || nicknameChecked);

    if (!isCompleted) {
      setIsOpen(true);
      if (emssUser.country_code) setSelectedCountry(emssUser.country_code);
      if (emssUser.priority_game_id) setSelectedGameId(emssUser.priority_game_id);
      if (emssUser.display_name) setNickname(emssUser.display_name);
    } else {
      setIsOpen(false);
    }
  }, [emssUser]);

  const handleSave = async () => {
    if (!selectedCountry || !selectedGameId) {
      toast.error(t('common.fill_all_fields') || 'Por favor, preencha todos os campos.');
      return;
    }

    const countryName = COUNTRIES.find(c => c.code === selectedCountry)?.name || '';

    setIsSaving(true);
    try {
      await updateProfile({
        country_code: selectedCountry,
        priority_game_id: selectedGameId,
        location: countryName,
        privacy_policy_version: '1.0',
        privacy_policy_accepted_at: new Date().toISOString(),
        ...(nickname.trim() && { display_name: nickname.trim() }),
      });
      if (emssUser?.id) {
        localStorage.setItem(NICKNAME_CHECKED_KEY, emssUser.id);
      }
      toast.success(t('settings.success'));
      setIsOpen(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(t('settings.save_error'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSkipNickname = async () => {
    if (emssUser?.country_code && emssUser?.priority_game_id) {
      setIsSaving(true);
      try {
        await updateProfile({
          privacy_policy_version: '1.0',
          privacy_policy_accepted_at: new Date().toISOString(),
        });
        if (emssUser?.id) {
          localStorage.setItem(NICKNAME_CHECKED_KEY, emssUser.id);
        }
        setIsOpen(false);
      } catch (error) {
        console.error('Error updating profile:', error);
      } finally {
        setIsSaving(false);
      }
    }
  };

  // Derived
  const hasRequiredFields = !!selectedCountry && !!selectedGameId;
  const hasNickname = !!emssUser?.display_name;
  const isExistingUser = !!emssUser?.country_code && !!emssUser?.priority_game_id;

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 overflow-hidden">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/80 backdrop-blur-md"
        />

        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          className="relative w-full max-w-md bg-console-bg border border-white/10 rounded-[2.5rem] overflow-hidden shadow-[0_0_50px_rgba(0,0,0,0.5)]"
        >
          <div className="h-2 w-full bg-gradient-to-r from-game-primary via-white/50 to-game-primary animate-pulse" />

          <div className="p-8 md:p-10">
            <div className="flex flex-col items-center text-center space-y-6">
              <div className="relative">
                <div className="absolute inset-0 bg-game-primary/20 blur-2xl rounded-full" />
                <div className="relative w-20 h-20 bg-game-primary/10 rounded-3xl flex items-center justify-center border border-game-primary/20">
                  <Globe size={40} className="text-game-primary animate-pulse" />
                </div>
              </div>

              <div className="space-y-2">
                <h2 className="text-3xl font-condensed font-black italic uppercase tracking-tighter text-white">
                  {t('onboarding.title')}
                </h2>
                <p className="text-xs font-label uppercase tracking-[0.2em] text-console-muted leading-relaxed">
                  {t('onboarding.subtitle')}
                </p>
              </div>

              <div className="w-full space-y-4 pt-4">
                {/* Nickname Field */}
                <div className="space-y-2 text-left">
                  <label className="text-[10px] font-label uppercase tracking-widest text-console-muted ml-1 flex items-center gap-1.5">
                    <UserCircle2 size={12} className="text-game-primary" />
                    {t('onboarding.nickname')}
                  </label>
                  <div className="relative group">
                    <input
                      type="text"
                      value={nickname}
                      onChange={(e) => setNickname(e.target.value)}
                      disabled={isSaving}
                      placeholder={t('onboarding.nickname_placeholder')}
                      className="w-full bg-console-surface border border-white/5 rounded-2xl p-4 text-sm text-white focus:outline-none focus:ring-2 focus:ring-game-primary/50 focus:border-game-primary/50 transition-all group-hover:border-white/10 placeholder:text-white/20"
                    />
                  </div>
                  {/* Explanatory hint */}
                  <div className="flex items-start gap-2 bg-game-primary/5 border border-game-primary/15 rounded-xl px-3 py-2.5 mt-1">
                    <Info size={12} className="text-game-primary shrink-0 mt-[1px]" />
                    <p className="text-[9px] font-label leading-relaxed text-console-muted uppercase tracking-wide">
                      {t('onboarding.nickname_hint')}
                    </p>
                  </div>
                </div>

                {/* Nationality Selector */}
                <div className="space-y-2 text-left">
                  <label className="text-[10px] font-label uppercase tracking-widest text-console-muted ml-1">
                    {t('onboarding.nationality')}
                  </label>
                  <div className="relative group">
                    <select
                      value={selectedCountry}
                      onChange={(e) => setSelectedCountry(e.target.value)}
                      disabled={isSaving}
                      className="w-full bg-console-surface border border-white/5 rounded-2xl p-4 text-sm text-white appearance-none focus:outline-none focus:ring-2 focus:ring-game-primary/50 focus:border-game-primary/50 transition-all cursor-pointer group-hover:border-white/10"
                    >
                      <option value="" disabled>{t('settings.select_country') || 'Selecione seu país...'}</option>
                      {COUNTRIES.map((c) => (
                        <option key={c.code} value={c.code} className="bg-console-surface text-white">
                          {c.name}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-console-muted">
                      <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Game Selector */}
                <div className="space-y-2 text-left">
                  <label className="text-[10px] font-label uppercase tracking-widest text-console-muted ml-1">
                    {t('onboarding.priority_game')}
                  </label>
                  <div className="relative group">
                    <select
                      value={selectedGameId}
                      onChange={(e) => setSelectedGameId(e.target.value)}
                      disabled={isSaving}
                      className="w-full bg-console-surface border border-white/5 rounded-2xl p-4 text-sm text-white appearance-none focus:outline-none focus:ring-2 focus:ring-game-primary/50 focus:border-game-primary/50 transition-all cursor-pointer group-hover:border-white/10"
                    >
                      <option value="" disabled>{t('settings.select_game')}</option>
                      {GAMES.map((game) => (
                        <option key={game.id} value={game.id} className="bg-console-surface text-white">
                          {game.name}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-console-muted">
                      <Gamepad2 size={16} />
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleSave}
                  disabled={!hasRequiredFields || isSaving}
                  className={`
                    w-full py-4 rounded-2xl font-label text-sm font-black uppercase tracking-[0.3em] flex items-center justify-center gap-3 transition-all duration-300 mt-4
                    ${(!hasRequiredFields || isSaving)
                      ? 'bg-console-surface text-console-muted cursor-not-allowed opacity-50 border border-white/5'
                      : 'bg-game-primary text-black hover:scale-[1.02] hover:brightness-110 active:scale-[0.98] shadow-[0_0_30px_rgba(var(--game-primary-rgb),0.4)] cursor-pointer'}
                  `}
                >
                  {isSaving ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      {t('settings.saving')}
                    </>
                  ) : (
                    <>
                      <Save size={18} />
                      {t('onboarding.start_career')}
                    </>
                  )}
                </button>

                {/* Skip link — only for existing users who already have country/game */}
                {isExistingUser && hasNickname && (
                  <button
                    onClick={handleSkipNickname}
                    disabled={isSaving}
                    className="w-full text-[9px] font-label uppercase tracking-[0.2em] text-console-muted hover:text-white/60 transition-colors py-1"
                  >
                    {t('onboarding.nickname_already_correct')}
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white/[0.02] py-4 text-center border-t border-white/[0.05]">
            <span className="text-[10px] font-label text-white/20 uppercase tracking-[0.5em]">
              e-MSS Performance Console
            </span>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default CountryCheckModal;
